// R-3 pre-onboarding flow. Source of truth: docs/specs/sprint-3/stage-1-contracts.md §6.3 + stage-4-screens.md §3.3.
//
// Используется (а) на /, (б) после login mutation'а. При mount'е считает
// useOnboardingState() и редиректит в правильный шаг onboarding'а.
//
// Для verifyPending редирект на /verify-code (OTP-флоу, D12); S-7 сам проверит
// наличие AwaitVerifyContext и при пустом cache'е сделает fallback /signin
// (stage-4 §5.7 — explicit guard в S-7).

import { useEffect } from 'react';
import { Redirect, useLocation } from 'wouter';
import type { ReactNode } from 'react';
import { useOnboardingState, type OnboardingState } from '@/hooks/useOnboardingState';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { HomeConfirmed } from '@/pages/HomeConfirmed';
import { HomeEmpty } from '@/pages/HomeEmpty';
import { DashboardErrorView } from '@/components/DashboardErrorView';

export function pathForState(state: OnboardingState): string {
  switch (state.kind) {
    case 'unauth':
      return '/signin';
    case 'verifyPending':
      return '/verify-code';
    case 'consentNeeded':
      return '/consent';
    case 'surveyPending':
      return `/survey/step-${state.resumeStep}`;
    case 'uploadAwaitingReview':
    case 'uploadConfirmed1':
    case 'uploadConfirmedMany':
    case 'dashboardError':
      // Все Sprint 5 confirmed/error состояния живут inline на `/` — OnboardingRouter
      // рендерит соответствующий компонент без редиректа.
      return '/';
    case 'surveyDone':
      return '/';   // Sprint 5A.1 — HomeEmpty inline на / через OnboardingRouter
  }
}

// Компонент-перенаправитель. На /, который рендерит Landing для unauth и
// pushes onboarding-target для всего остального — используется через
// `<OnboardingRouter unauthFallback={<Landing/>}/>`.
export function OnboardingRouter({ unauthFallback }: { unauthFallback?: ReactNode }) {
  const { state, isLoading } = useOnboardingState();
  if (isLoading) return <FullScreenSpinner />;
  if (!state) return <Redirect to="/signin" />;
  if (state.kind === 'unauth') return <>{unauthFallback ?? <Redirect to="/signin" />}</>;
  // HEAL-46a D-18: ветки `uploadAwaitingReview` здесь больше нет.
  //
  // Со Sprint 4 непроверенный анализ подменял Главную целиком — экран проверки
  // рендерился прямо тут. При пакете это ловушка: человек заперт до конца
  // разбора, а кнопка «На Главную» возвращала его сюда же. Теперь проверка
  // живёт на своём адресе, а на Главной от неё остаётся строка-вход.
  // Sprint 5 (Q-S5-S4-4): оба confirmed state'а → единый HomeConfirmed inline:
  if (state.kind === 'uploadConfirmed1' || state.kind === 'uploadConfirmedMany') {
    return <HomeConfirmed />;
  }
  // RF-S5-S4-4: 5xx/network на /dashboard → retry view, НЕ маскирование через redirect.
  if (state.kind === 'dashboardError') {
    return <DashboardErrorView />;
  }
  // Sprint 5A.1 (H1 fix): HomeEmpty inline на / — был отдельный /survey/done route,
  // теперь главная для empty-state. Маршрут /survey/done сохранён в App.tsx с
  // <Redirect to="/" replace /> для back-compat (wouter 3.9.0 default replace=false
  // создавал back-button loop).
  if (state.kind === 'surveyDone') {
    return <HomeEmpty />;
  }
  return <Redirect to={pathForState(state)} />;
}

// Imperative-вариант: используется на странице после login'а для редиректа
// без unmount'а текущей route. Возвращает null после push'а.
export function useOnboardingRedirect(): void {
  const { state } = useOnboardingState();
  const [location, setLocation] = useLocation();
  useEffect(() => {
    if (!state || state.kind === 'unauth') return;
    const target = pathForState(state);
    if (location !== target) setLocation(target);
  }, [state, location, setLocation]);
}
